import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import extractTourV2PurchasableOptionDetails from 'lib/tours/extractTourV2PurchasableOptionDetails'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { getTourPricePerDay, getTourV2VariationDuration, getTourValueAtPerDay } from 'lib/tours/tourUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'

interface Props {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  variation?: App.Tours.TourV2OfferVariation
  filters: App.OfferListFilters
}

function TourV2SearchTilePriceStack(props: Props) {
  const { offer, variation } = props
  const intl = useIntl()
  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const tourPriceMode = useAppSelector(getTourPriceMode)
  const option = useMemo(() => {
    if (variation) {
      return findCheapestTourV2PurchasableOption(offer, variation.id)
    }
  }, [offer, variation])
  const details = useMemo(() => {
    if (option) {
      return extractTourV2PurchasableOptionDetails(option)
    }
  }, [option])

  const duration = useMemo<number | undefined>(() => {
    if (variation) {
      return getTourV2VariationDuration(variation)
    }
  }, [variation])

  const pricePerDayDetails = useMemo(() => {
    if (!details || tourPriceMode === 'total') return
    return {
      valueAtPerDay: getTourValueAtPerDay(details.valuedAt?.price, duration),
      pricePerDay: getTourPricePerDay(details?.pricePerPerson, details?.memberPricePerPerson, duration),
    }
  }, [details, duration, tourPriceMode])

  const soldOutLabel = intl.formatMessage({
    id: 'VuKOhX0wPR0FhH2J_o4N',
    defaultMessage: 'Sold out',
    description: 'Label shown when tour offer is sold out on the tour offer detail page',
  })

  if (!details || !option) {
    return <BodyText variant="medium">{soldOutLabel}</BodyText>
  }

  return <div>
    {!!duration && <PriceRowPriceCaption>
      {intl.formatMessage(
        {
          id: 'I3V9MaD8STNtfxzYjqJ_',
          defaultMessage: '{count, plural, one {# day} other {# days}} from',
          description: 'Tour duration caption before the price, e.g. "3 days from"',
        },
        { count: duration },
      )}
    </PriceRowPriceCaption>}
    {!pricePerDayDetails?.pricePerDay && <LuxPlusPriceStack
      size="L"
      price={details.pricePerPerson}
      saleUnit="person"
      memberPrice={details.memberPrice}
      isTour
    />}
    {!!pricePerDayDetails?.pricePerDay && <LuxPlusPriceStack
      size="L"
      price={pricePerDayDetails.pricePerDay.pricePerPersonPerDay}
      saleUnit="day per person"
      memberPrice={pricePerDayDetails.pricePerDay.memberPricePerPersonPerDay}
      isTour
    />}
    {details.hasDiscount && <PriceRowSale value={details.fullPricePerPerson} />}
    {
      !!details.valuedAt && <>
        {!pricePerDayDetails?.valueAtPerDay && <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={details.valuedAt.price}
          discountPercentage={isLuxPlusMember ? details.valuedAt.memberSavingsPercentage : details.valuedAt.savingPercentage}
        />}

        {!!pricePerDayDetails?.valueAtPerDay && <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={pricePerDayDetails?.valueAtPerDay}
          discountPercentage={isLuxPlusMember ? details.valuedAt.memberSavingsPercentage : details.valuedAt.savingPercentage}
        />}
      </>
    }
    <PriceRowCaption>{details.roomTypeName}</PriceRowCaption>
    <PriceRowTaxesAndFees />
    <PriceRowAgentHubCommission
      productType={offer.productType}
      offerId={offer.id}
      offerLocations={variation ? [variation.startLocation!] : undefined}
    />
  </div>
}

export default TourV2SearchTilePriceStack
